/**
 * One request per site, shared by whoever asks first.
 *
 * The router calls `fetchSite` the moment it sees a /<space>/site URL — before React mounts and in
 * parallel with downloading SiteApp's code — and SiteApp's hook then reads the same promise. So a
 * visit costs one round trip for data, overlapped with the one for code, and nothing waits on the
 * other. Kept tiny and import-free so the router can carry it without pulling the app's weight in.
 *
 * The endpoint (api/site/[slug].js) answers with exactly what a signed-out visitor may read, already
 * narrowed to what the site reaches, and with cache headers a CDN can serve from the edge.
 */
const pending = new Map();

/** Where this browser remembers a site's colour, so a slow load's fallback line is drawn in it. */
export const siteColorKey = (slug) => `arcos:siteColor:${slug}`;

/**
 * Take the Arcos hub's boot splash away, at once. A site never shows it, and site.html never paints
 * it, but a site reached through the app shell can inherit one that index.html already painted —
 * and since only the hub's routes mount SplashController, nothing else would ever lift it: the
 * splash sat over the site at z-index 9999, invisible to a script and swallowing every scroll.
 */
export function dismissHubSplash() {
  if (typeof window === 'undefined') return;
  if (window.__bootDeadman) { clearTimeout(window.__bootDeadman); window.__bootDeadman = null; }
  document.getElementById('boot')?.remove();
}

/** @returns {Promise<{ missing: true } | { siteId: string, rows: object[], types: object[] }>} */
export function fetchSite(slug) {
  if (!slug) return Promise.resolve({ missing: true });
  // A page served by /api/site-page carries its site's data inline: no request at all.
  if (!pending.has(slug) && typeof window !== 'undefined' && window.__SITE_DATA__ && window.__SITE__?.slug === slug) {
    pending.set(slug, Promise.resolve(window.__SITE_DATA__));
    window.__SITE_DATA__ = null;
  }
  if (!pending.has(slug)) {
    // site.html starts this request inline, before any bundle loads; use it if it is for this site.
    const early = typeof window !== 'undefined' && window.__sitePrefetch?.slug === slug ? window.__sitePrefetch.res : null;
    if (early) window.__sitePrefetch = null;
    const p = (early || fetch(`/api/site/${encodeURIComponent(slug)}`, { headers: { accept: 'application/json' } }))
      .then(async (res) => {
        if (res.status === 404) return { missing: true };
        if (!res.ok) throw new Error(`site ${slug}: ${res.status}`);
        return res.json();
      })
      .catch((err) => { pending.delete(slug); throw err; });
    pending.set(slug, p);
  }
  return pending.get(slug);
}
